/** @jsx jsx */
import { jsx } from '@emotion/core';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Icon } from '../../ui/Icon';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import { debounce } from '../../../utils/debounce';
import getComponent from '../../../utils/getComponent';
import PriceGenerator from '../PriceGenerator';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import SolutionCardType from './SolutionCardType';
import { COMPONENT_NAMES } from '../../../utils/constants';
import {
  ctaArrowCss,
  headlineCss,
  priceCss,
  priceLightCss,
  bodyCss,
  goodForTitleCss,
  goodForCss,
  goodForBlackCss,
  goodForOuterWrapper,
  chevronCss,
  chevronRotatedCss,
  goodForBlackCaretCss,
  goodForWrapper,
  goodForExpandedCss,
  goodForItemsListCss,
  goodForItemsListExpandedCss,
  goodForItemCss,
  itemCheckCss,
  ctaCss,
  ctaLabelCss,
  buttonResetCss,
} from './index.styles';

export const defaultData = {
  Headline: '',
  SolutionPrice: null,
  ShortDescription: '',
  ShowAsBlackCard: false,
  Items: [],
  IsSolutionCard: false,
  Cta: {
    href: '',
    target: '',
    text: '',
  },
  Brand: '',
  RelatedItemForm: {
    value: '',
  },
};

export class SolutionCard extends SafePureComponent {
  constructor(props) {
    super(props);

    const locationHeader = getComponent(
      get(this.props, 'sitecoreContext'),
      COMPONENT_NAMES.locationHeader,
    );

    this.t = props.t;
    this.state = {
      goodForExpanded: false,
      displayCaret: true,
      location: get(locationHeader, 'fields.ProtonName.value', ''),
    };
  }

  expand(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ goodForExpanded: !this.state.goodForExpanded });
  }

  updateDimensions = () => {
    if (window.innerWidth < Breakpoints.lg) {
      this.setState({ goodForExpanded: false, displayCaret: true });
    } else {
      this.setState({ goodForExpanded: true, displayCaret: false });
    }
  };

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', debounce(this.updateDimensions, 300));
  }

  componentWillUnmount() {
    window.removeEventListener('resize', debounce(this.updateDimensions, 300));
  }

  renderGoodForItem = (item, index) => {
    return (
      <li css={goodForItemCss} key={`goodFor-${index}`}>
        <span>
          <Icon customCss={itemCheckCss} name="check" />
        </span>
        <span>{item}</span>
      </li>
    );
  };

  generateldJSON = fields => {
    const ldJson = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: fields.Headline,
      description: fields.ShortDescription,
    };

    if (fields.brand) {
      ldJson.brand = fields.brand;
    }
    if (fields.SolutionPrice) {
      const { Currency, Price } = fields.SolutionPrice;
      ldJson.offers = {
        '@type': 'Offer',
        priceCurrency: Currency ? Currency : '',
        price: Price > 0 ? Price : '',
      };
    }

    return JSON.stringify(ldJson);
  };

  generateHelmetScript = fields => {
    return (
      fields.IsSolutionCard && (
        <Helmet>
          <script type="application/ld+json">
            {this.generateldJSON(fields)}
          </script>
        </Helmet>
      )
    );
  };

  featureTransformations = displayCaret => {
    const FeatureElement = displayCaret ? 'button' : 'div';
    const dynamicFeatureProperties = {
      onClick: displayCaret ? e => this.expand(e) : null,
    };

    return [FeatureElement, dynamicFeatureProperties];
  };

  /**
   * Creates a markup to simulate visually a CTA
   * @param {function} t
   * @param {Object} fields element simulating structure of a link
   * @return {React.Node}
   */
  renderCta = (t, fields) => {
    const { Cta, ShowAsBlackCard } = fields;
    const href = get(Cta, 'href', '');
    const label = t('LocationCardCta');

    return (
      <div css={ctaCss(ShowAsBlackCard)}>
        <span css={ctaLabelCss(ShowAsBlackCard)}>
          {href ? get(Cta, 'text', label) : label}
        </span>
        <Icon customCss={ctaArrowCss} name="arrow-right" />
      </div>
    );
  };

  safeRender() {
    const fields = defaultsDeep(this.props.fields, defaultData);
    const { goodForExpanded, displayCaret, location } = this.state;
    const ctaHref = get(fields, 'Cta.href');
    const [
      FeatureElement,
      dynamicFeatureProperties,
    ] = this.featureTransformations(displayCaret);

    // Non free cards or cards with an href defined are clickable
    const isClickable = Boolean(ctaHref) || fields.SolutionPrice;

    return (
      <SolutionCardType fields={fields} location={location}>
        {this.generateHelmetScript(fields)}
        <div>
          <div>
            <h3 css={headlineCss}>{fields.Headline}</h3>
            {fields.SolutionPrice && (
              <span css={[priceCss, fields.ShowAsBlackCard && priceLightCss]}>
                {<PriceGenerator price={fields.SolutionPrice} />}
              </span>
            )}
            <div css={bodyCss}>{fields.ShortDescription}</div>
          </div>
          <div css={goodForOuterWrapper}>
            {/* on small devices is expected to behave as a expand collapse trigger element, and on large as a list */}
            <FeatureElement
              {...dynamicFeatureProperties}
              css={(goodForTitleCss, displayCaret && buttonResetCss)}
            >
              <span
                css={[goodForCss, fields.ShowAsBlackCard && goodForBlackCss]}
              >
                {this.t('GoodFor')}
              </span>
              {displayCaret && (
                <Icon
                  customCss={[
                    chevronCss,
                    goodForExpanded ? chevronRotatedCss : {},
                    fields.ShowAsBlackCard && goodForBlackCaretCss,
                  ]}
                  name="chevron-down"
                />
              )}
            </FeatureElement>
            <div
              css={[goodForWrapper, goodForExpanded ? goodForExpandedCss : {}]}
            >
              <ul
                css={[
                  goodForItemsListCss,
                  goodForExpanded ? goodForItemsListExpandedCss : {},
                ]}
              >
                {fields.Items &&
                  fields.Items.map(
                    (item, i) =>
                      !isEmpty(item) && this.renderGoodForItem(item, i),
                  )}
              </ul>
            </div>
          </div>
        </div>
        {isClickable && this.renderCta(this.t, fields)}
      </SolutionCardType>
    );
  }
}

SolutionCard.displayName = 'SolutionCard';

SolutionCard.defaultProps = {
  fields: defaultData,
  t: key => key,
};

SolutionCard.propTypes = {
  fields: PropTypes.shape({
    Brand: PropTypes.string,
    Headline: PropTypes.string,
    SolutionPrice: PropTypes.shape({
      Currency: PropTypes.string,
      Price: PropTypes.number,
      PriceUnit: PropTypes.string,
    }),
    ShortDescription: PropTypes.string,
    ShowAsBlackCard: PropTypes.bool,
    Items: PropTypes.arrayOf(PropTypes.string),
    IsSolutionCard: PropTypes.bool,
    Cta: PropTypes.shape({
      href: PropTypes.string,
      target: PropTypes.string,
      text: PropTypes.string,
    }),
    RelatedItemForm: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
  t: PropTypes.func,
};

export default compose(withNamespaces(), withSitecoreContext())(SolutionCard);
